import { PickSampleExt, View } from "@novorender/api";
import { ReadonlyVec3 } from "gl-matrix";

import { getSurfaceProjection, SurfaceProjectionSize } from "features/render/surfaceProjection";
import { isRealVec } from "utils/misc";

import { SECTION_PLANE_DARK_GREEN, SECTION_PLANE_NEON } from "./sectionPlaneColors";

const SECTION_PLANE_CURSOR_SURFACE_CENTER_X = 6.432;
const SECTION_PLANE_CURSOR_SURFACE_CENTER_Y = 16.157;
const SECTION_PLANE_CURSOR_BASE_SURFACE_RATIO = 0.36;
const SECTION_PLANE_CURSOR_SCALE = 1.8;
const SECTION_PLANE_CURSOR_SURFACE_PATH =
    "M11.93 16.306c-.118 4.373-.891 8.29-2.027 11.087-.569 1.4-1.219 2.494-1.902 3.223-.683.73-1.356 1.053-1.99 1.035-.633-.017-1.288-.376-1.93-1.141-.642-.766-1.232-1.893-1.724-3.322C1.374 24.334.816 20.38.934 16.008c.12-4.373.892-8.29 2.028-11.087.569-1.4 1.219-2.494 1.902-3.223C5.547.968 6.22.646 6.854.663s1.287.376 1.93 1.142c.642.765 1.232 1.892 1.724 3.321.983 2.854 1.541 6.808 1.422 11.18Z";
export const SECTION_PLANE_CURSOR_ARROW_PATH =
    "M23.342 21.568a1 1 0 0 1-1.376-1.452l1.727-1.636a.5.5 0 0 0-.33-.863l-10.238-.278a.5.5 0 0 0-.376.844l1.635 1.726a1 1 0 0 1-1.453 1.376l-4.125-4.356a1 1 0 0 1 .038-1.414L13.2 11.39a1 1 0 0 1 1.376 1.452l-1.728 1.636a.5.5 0 0 0 .33.863l10.24.278a.5.5 0 0 0 .377-.843l-1.637-1.728a1 1 0 1 1 1.452-1.375l4.127 4.357a1 1 0 0 1-.039 1.413z";
export const SECTION_PLANE_CURSOR_ARROW_OUTLINE_PATH =
    "m27.737 16.029.726-.688zm-4.127-4.357.726-.688zm-1.452 1.375.726-.688zm-9.31 1.43.688.726zm.352-3.088-.687-.726zm-4.394 5.54.726-.688zm.038-1.414-.688-.726zm5.501 5.808.687.727zm-1.414-.038-.726.688zm-.182-3.102-.726.688zm9.179 3.346.726-.687zm1.413.039-.687-.726zm-1.375-1.452.688.726 1.727-1.636-.688-.726-.688-.726-1.726 1.636zm1.397-2.498.027-1-10.237-.278-.028 1-.027.999 10.238.278zm-10.614.565-.726.688 1.635 1.726.726-.688.726-.687-1.635-1.727zm.182 3.102.726-.688-4.125-4.356-.726.688-.726.688 4.125 4.355zm-4.087-5.77.688.726 4.356-4.126-.688-.726-.687-.726-4.357 4.126zm5.732-2.674-.688-.726-1.727 1.636.688.726.687.726 1.728-1.636zm-1.397 2.499-.027 1 10.239.278.027-1 .027-1-10.24-.278zm10.616-.565.726-.688-1.637-1.728-.726.688-.726.688 1.637 1.727zm-.185-3.103-.726.688 4.127 4.356.726-.687.726-.688-4.127-4.357zm4.088 5.77-.687-.726-4.357 4.126.688.726.687.726 4.357-4.126zm.038-1.413-.726.687.688.726.688.726a2 2 0 0 0 .077-2.827zm-5.54-4.396.688.727.726-.688.726-.688a2 2 0 0 0-2.827-.077zm-.038 1.414.726-.688-.687-.726-.688-.726a2 2 0 0 0-.077 2.828zm1.26 2.571-.027 1c1.336.036 2.049-1.56 1.13-2.531l-.726.688-.726.687a.5.5 0 0 1 .376-.843zm-10.57-1.14-.687-.727c-.97.92-.345 2.552.99 2.589l.028-1 .027-1a.5.5 0 0 1 .33.863zm1.766-3.05-.726.687.688.726.688.726a2 2 0 0 0 .076-2.828zm-1.414-.04.688.727.726-.688.726-.688a2 2 0 0 0-2.827-.076zm-4.394 5.54.726-.687-.688-.726-.688-.726a2 2 0 0 0-.076 2.827zm5.539 4.395-.687-.726-.727.688-.726.688a2 2 0 0 0 2.827.077zm.039-1.414-.726.688.687.726.687.727a2 2 0 0 0 .078-2.828zm-1.259-2.57.028-1c-1.336-.036-2.05 1.561-1.13 2.532l.726-.688.726-.688a.5.5 0 0 1-.377.844zm10.568 1.141.688.726c.97-.919.345-2.552-.991-2.588l-.027 1-.027 1a.5.5 0 0 1-.33-.864zm-1.765 3.05.726-.688-.688-.726-.687-.726a2 2 0 0 0-.077 2.827zm0 0-.726.687a2 2 0 0 0 2.827.077l-.687-.726-.688-.726z";
const SECTION_PLANE_CURSOR_MASK_ID = "section-plane-cursor-arrow-mask";

export function createSectionPlaneCursorSvg(
    pickResult: PickSampleExt | undefined,
    x: number,
    y: number,
    size: SurfaceProjectionSize,
    view: View,
): string {
    const cursorGeometry = getSectionPlaneCursorGeometry(pickResult, x, y, size, view);
    const centerTransform = `translate(-${SECTION_PLANE_CURSOR_SURFACE_CENTER_X},-${SECTION_PLANE_CURSOR_SURFACE_CENTER_Y})`;
    const rotateTransform = `rotate(${cursorGeometry.arrowAngle.toFixed(2)})`;

    return `
        <g data-section-plane-cursor transform="scale(${SECTION_PLANE_CURSOR_SCALE})">
            <g data-section-plane-surface>
                <g data-section-plane-surface-orientation transform="${rotateTransform}">
                    <g data-section-plane-surface-projection transform="scale(${cursorGeometry.surfaceScaleX.toFixed(3)} 1)">
                        <g data-section-plane-surface-center transform="${centerTransform}">
                            <path fill="none" stroke="${SECTION_PLANE_NEON}" d="${SECTION_PLANE_CURSOR_SURFACE_PATH}"/>
                            <ellipse cx="6.432" cy="16.157" fill="${SECTION_PLANE_DARK_GREEN}" opacity=".6" rx="7" ry="2" transform="rotate(91.556 6.432 16.157)"/>
                        </g>
                    </g>
                </g>
            </g>
            <g data-section-plane-arrow transform="${rotateTransform}">
                <g data-section-plane-arrow-center transform="${centerTransform}">
                    <mask id="${SECTION_PLANE_CURSOR_MASK_ID}" width="22.345" height="13.593" x="7.26" y="9.53" fill="#000" maskUnits="userSpaceOnUse">
                        <path fill="#fff" d="M7.26 9.53h22.345v13.593H7.26z"/>
                        <path d="${SECTION_PLANE_CURSOR_ARROW_PATH}"/>
                    </mask>
                    <path data-section-plane-arrow-fill fill="${SECTION_PLANE_DARK_GREEN}" d="${SECTION_PLANE_CURSOR_ARROW_PATH}"/>
                    <path fill="${SECTION_PLANE_NEON}" d="${SECTION_PLANE_CURSOR_ARROW_OUTLINE_PATH}" mask="url(#${SECTION_PLANE_CURSOR_MASK_ID})"/>
                </g>
            </g>
        </g>
    `;
}

function getSectionPlaneCursorGeometry(
    pickResult: PickSampleExt | undefined,
    x: number,
    y: number,
    size: SurfaceProjectionSize,
    view: View,
): { arrowAngle: number; surfaceScaleX: number } {
    const surfaceProjection = getSurfaceProjection(pickResult?.normalVS, x, y, size, view);

    return {
        arrowAngle: getSectionPlaneCursorAngle(surfaceProjection?.normal),
        surfaceScaleX: getSectionPlaneSurfaceScaleX(surfaceProjection?.normal),
    };
}

export function getSectionPlaneSurfaceScaleX(normalVS?: ReadonlyVec3): number {
    if (!normalVS || !isRealVec(normalVS)) {
        return 1;
    }

    const normalLength = Math.hypot(normalVS[0], normalVS[1], normalVS[2]);
    if (normalLength === 0) {
        return 1;
    }

    const surfaceRatio = Math.min(
        1,
        Math.max(SECTION_PLANE_CURSOR_BASE_SURFACE_RATIO, Math.abs(normalVS[2]) / normalLength),
    );
    return surfaceRatio / SECTION_PLANE_CURSOR_BASE_SURFACE_RATIO;
}

function getSectionPlaneCursorAngle(normalVS?: ReadonlyVec3): number {
    if (!normalVS || !isRealVec(normalVS)) {
        return 0;
    }

    const screenNormalX = normalVS[0];
    const screenNormalY = -normalVS[1];
    const len2d = Math.hypot(screenNormalX, screenNormalY);
    if (len2d < 0.01) {
        return 0;
    }

    return (Math.atan2(screenNormalY, screenNormalX) * 180) / Math.PI;
}
